import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AUTOMATIC_SUPPORTED_UNITS_SCHEMA } from '@schuettflix/interfaces';
import { toAutomaticUnit } from '../utils/toAutomaticUnit.ts';
import { AutomaticProductsFormValues } from '../schema/getAutomaticProductsFormSchema.ts';
import { useAutomaticFormContext } from '../context/AutomaticFormContext.tsx';
import { SelectOrganizationInput } from '@/shared/inputs/SelectOrganizationInput.tsx';
import { EXCLUDED_PARTNER_ORGANIZATION_IDS } from '@/shared/constants/excludedPartnerOrganizations.ts';
import { TRANSPORT_EXCHANGE_VALUE } from '@/shared/constants/transportExchange.ts';
import { useFormLocations } from '../hooks/useFormLocations.ts';
import { useOrderFormQuantities } from '../hooks/useOrderFormQuantities.ts';
import { Fieldset, FieldsetBody, FieldsetColumn, FieldsetLabel } from '../components/Fieldset.tsx';
import { NegativePurchasePriceHint, ZeroPurchasePriceHint } from '@/shared/components/PriceInputHints.tsx';
import { UnitInput } from '@/shared/inputs/UnitInput.tsx';
import { PriceInput } from '@/shared/inputs/PriceInput.tsx';
import { TaxClassInput } from '@/shared/inputs/TaxClassInput.tsx';

export function OrderPurchaseTransportFieldset() {
    const methods = useFormContext<AutomaticProductsFormValues>();
    const { t } = useTranslation();
    const { taxClassOptions, defaultValuesLabels, formRules, customerOrganization } = useAutomaticFormContext();
    const {
        register,
        watch,
        setValue,
        getValues,
        formState: { defaultValues },
    } = methods;
    const { clearLocations } = useFormLocations(methods);
    const { onPurchaseTransportUnitChange } = useOrderFormQuantities(methods);

    const [
        purchaseTransportPrice,
        purchaseTransportUnit,
        currencyCode,
        showZeroPurchaseTransportPrice,
        carrierOrganizationId,
    ] = watch([
        'purchaseTransportPrice',
        'purchaseTransportUnit',
        'currencyCode',
        'showZeroPurchaseTransportPrice',
        'carrierOrganizationId',
    ]);

    const [carrierOrganizationName, setCarrierOrganizationName] = useState<string | undefined>(
        defaultValuesLabels?.carrierOrganization
    );

    const isCarrierCustomerOrg = carrierOrganizationId === customerOrganization.id;

    useEffect(() => {
        if (isCarrierCustomerOrg) {
            setValue('purchaseTransportPrice', 0);
            setValue('showZeroPurchaseTransportPrice', false);
        }
    }, [isCarrierCustomerOrg, setValue]);

    return (
        <Fieldset
            title={t('forms.automaticProductsForm.sections.transportPurchase')}
            dataTest="order-purchase-transport-fieldset"
        >
            <FieldsetBody className="@sm:grid-cols-3 @2xl:grid-cols-8 grid-cols-1">
                <FieldsetColumn
                    className="@2xl:col-span-4 col-span-full"
                    label={t('forms.automaticProductsForm.labels.carrier')}
                >
                    <SelectOrganizationInput
                        key={defaultValues?.carrierOrganizationId}
                        {...register('carrierOrganizationId')}
                        defaultValue={defaultValues?.carrierOrganizationId}
                        defaultValueLabel={defaultValuesLabels?.carrierOrganization}
                        placeholder={t('product.addPosition.carrier.label')}
                        exclude={{ ids: EXCLUDED_PARTNER_ORGANIZATION_IDS }}
                        filters={{ organizationType: 'carrier' }}
                        dataTest="carrier-organization-select-input"
                        specialOptions={[
                            {
                                value: TRANSPORT_EXCHANGE_VALUE,
                                label: t('forms.inputs.partnerSelectionInput.transportExchange'),
                            },
                        ]}
                        onValueChange={org => {
                            const carrierOrganizationId = getValues('carrierOrganizationId');
                            if (org && carrierOrganizationId && typeof carrierOrganizationId === 'number') {
                                clearLocations(carrierOrganizationId);
                            }
                            setCarrierOrganizationName(org?.name);
                        }}
                    />
                </FieldsetColumn>

                <FieldsetColumn className="@2xl:col-span-2">
                    <FieldsetLabel>{t('forms.automaticProductsForm.labels.unitPrice')}</FieldsetLabel>
                    <PriceInput
                        {...register('purchaseTransportPrice')}
                        unit={purchaseTransportUnit}
                        currencyCode={currencyCode}
                        disabled={isCarrierCustomerOrg}
                        testId="purchase-transport-price-input"
                    />
                </FieldsetColumn>

                <FieldsetColumn className="col-span-1">
                    <FieldsetLabel>{t('forms.automaticProductsForm.labels.unit')}</FieldsetLabel>
                    <UnitInput
                        {...register('purchaseTransportUnit')}
                        options={[...AUTOMATIC_SUPPORTED_UNITS_SCHEMA.options]}
                        disabled={formRules?.purchaseTransportUnit?.disabled}
                        onUnitChange={unit => onPurchaseTransportUnitChange(toAutomaticUnit(unit))}
                    />
                </FieldsetColumn>

                <FieldsetColumn className="@2xl:col-span-1">
                    <FieldsetLabel>{t('forms.automaticProductsForm.labels.taxClass')}</FieldsetLabel>
                    <TaxClassInput
                        {...register('purchaseTransportTaxClassId')}
                        options={taxClassOptions}
                        testId="purchase-transport-tax-class-input"
                    />
                </FieldsetColumn>

                {purchaseTransportPrice === 0 && !isCarrierCustomerOrg && (
                    <div className="@2xl:col-span-8 mt-3">
                        <ZeroPurchasePriceHint
                            partnerOrganizationName={carrierOrganizationName}
                            isBillingRelevant={showZeroPurchaseTransportPrice ?? false}
                            onClick={() =>
                                setValue('showZeroPurchaseTransportPrice', !(showZeroPurchaseTransportPrice ?? false))
                            }
                        />
                    </div>
                )}
                {Number(purchaseTransportPrice) < 0 && (
                    <div className="@2xl:col-span-8 mt-3">
                        <NegativePurchasePriceHint partnerOrganizationName={carrierOrganizationName} />
                    </div>
                )}
            </FieldsetBody>
        </Fieldset>
    );
}
